import {
  Box3,
  BoxGeometry,
  Color,
  Intersection,
  Line,
  LineSegments,
  MathUtils,
  Mesh,
  MeshBasicMaterial,
  Object3D,
  Plane,
  Points,
  Vector3,
  WebGLRenderer,
} from 'three';

import { SkyViewContextMenuProps } from '../../react/components/skyview/context-menu';
import { t } from '../../utils/i18-next';
import { isDefined } from '../../utils/variables';
import { AssetManager } from '../asset-manager';
import cameraManager from '../camera-manager';
import { CoordinateManager } from '../coordinate-manager';
import { Tiled3DModelHandle } from '../geodata/3d-tiled-models/tiled-3d-model-handle';
import { PointCloudHandle } from '../geodata/point-clouds/point-cloud-handle';
import { InputHandler } from '../input-handler';
import {
  MouseButtonType,
  MouseInputData,
} from '../utilities/input-event/input-event-data-extractor';
import { Coordinates, findClosestPointOnLine } from '../utilities/math-helpers';
import { setRayCasterParamsBasedOnCamera } from '../utilities/pick';
import { PickingSphere } from './picking-sphere';
import { Tool } from './tool';

const BOX_COLOR = new Color().setHSL(0.66, 1, 0.5);
const GIZMO_DEFAULT_COLOR = new Color().setHSL(0.66, 1, 0.5);
const GIZMO_HOVER_COLOR = new Color().setHSL(0.8, 1, 0.5);
const GIZMO_CENTER_COLOR = new Color().setHSL(0, 0, 0.5);

/** Margin in world units to consider the cursor "over" the box for showing gizmos (more lenient hit test). */
const BOX_HIT_MARGIN = 1;

export type HoveredGizmo =
  | { kind: 'face'; index: number }
  | { kind: 'center' }
  | { kind: 'rotation'; index: 0 | 1 | 2 | 3 }
  | undefined;

export class IntersectionTool extends Tool {
  contextMenuCallback?: (contextMenu?: SkyViewContextMenuProps) => void = undefined;

  cursor = new PickingSphere();

  /** Clipping plane normals and planes; normals updated from boxFrame + halfSize when intersection is active. */
  private clippingPlaneNormals = [
    new Vector3(1, 0, 0),
    new Vector3(-1, 0, 0),
    new Vector3(0, 1, 0),
    new Vector3(0, -1, 0),
    new Vector3(0, 0, 1),
    new Vector3(0, 0, -1),
  ];
  clippingPlanes = this.clippingPlaneNormals.map((n) => new Plane(n.clone(), 0));
  private planePoint = new Vector3();
  private rayClosestPoint = new Vector3();
  private gizmoWorldPos = new Vector3();
  private dragPlane = new Plane();
  private dragLineStart = new Vector3();
  private dragLineEnd = new Vector3();
  private rayIntersectPoint = new Vector3();
  private expandedPickBox = new Box3();
  lastIntersection: Intersection | undefined;

  /** When true, the user has confirmed the intersection (Create); clipping and point test use boxFrame + halfSize. */
  intersectionActive = false;

  /** Transform frame: position = box center, rotation = box orientation (e.g. rotation.z). */
  boxFrame = new Object3D();
  /** Half-extents in local space; full size = halfSize * 2. */
  halfSize = new Vector3(0.5, 0.5, 0.5);

  /** Set by the intersection tool menu so the tool can update renderer clipping planes. */
  webGlRenderer: WebGLRenderer | undefined = undefined;

  private boxCenterProjectCoords: Coordinates | undefined = undefined;

  private saveBoxCenter(): void {
    this.boxCenterProjectCoords = CoordinateManager.getInstance().localToProjectCoords(
      this.boxFrame.position,
    );
  }

  private static readonly CLIPPING_PLANE_EPSILON = 1e-4;
  /** Minimum half-extent per axis; full size is at least 2 * MIN_BOX_HALF_SIZE on each axis. */
  private static readonly MIN_BOX_HALF_SIZE = 3 * IntersectionTool.CLIPPING_PLANE_EPSILON;

  private boxGeometry = new BoxGeometry(1, 1, 1);
  private boxMaterial = new MeshBasicMaterial({
    color: BOX_COLOR,
    wireframe: true,
  });
  intersectionBox = new Mesh(this.boxGeometry, this.boxMaterial);

  private frontGizmo = new PickingSphere(GIZMO_DEFAULT_COLOR, 10, 'circle');
  private backGizmo = new PickingSphere(GIZMO_DEFAULT_COLOR, 10, 'circle');
  private leftGizmo = new PickingSphere(GIZMO_DEFAULT_COLOR, 10, 'circle');
  private rightGizmo = new PickingSphere(GIZMO_DEFAULT_COLOR, 10, 'circle');
  private topGizmo = new PickingSphere(GIZMO_DEFAULT_COLOR, 10, 'circle');
  private bottomGizmo = new PickingSphere(GIZMO_DEFAULT_COLOR, 10, 'circle');
  /** Central gizmo for moving the whole box (gray). */
  centerGizmo = new PickingSphere(GIZMO_CENTER_COLOR, 15, 'round');
  /** Rotation gizmos at the four top corners (y = +hy): front-right, front-left, back-right, back-left. */
  private topFrontRightGizmo = new PickingSphere(GIZMO_DEFAULT_COLOR, 10, 'triangle');
  private topFrontLeftGizmo = new PickingSphere(GIZMO_DEFAULT_COLOR, 10, 'triangle');
  private topBackRightGizmo = new PickingSphere(GIZMO_DEFAULT_COLOR, 10, 'triangle');
  private topBackLeftGizmo = new PickingSphere(GIZMO_DEFAULT_COLOR, 10, 'triangle');
  readonly rotationGizmos = [
    this.topFrontRightGizmo,
    this.topFrontLeftGizmo,
    this.topBackRightGizmo,
    this.topBackLeftGizmo,
  ] as const;
  readonly faceGizmos = [
    this.frontGizmo,
    this.backGizmo,
    this.leftGizmo,
    this.rightGizmo,
    this.topGizmo,
    this.bottomGizmo,
  ] as const;

  /** Face normals in same order as faceGizmos: front(+Z), back(-Z), left(-X), right(+X), top(+Y), bottom(-Y) */
  private static readonly faceNormals = [
    new Vector3(0, 0, 1),
    new Vector3(0, 0, -1),
    new Vector3(-1, 0, 0),
    new Vector3(1, 0, 0),
    new Vector3(0, 1, 0),
    new Vector3(0, -1, 0),
  ] as const;

  private static readonly gizmoDragLineHalfLength = 1e6;

  /** When set, we are dragging this face (index into faceGizmos / faceNormals). */
  draggingFaceIndex: number | undefined = undefined;

  /** When set, we are dragging the center gizmo: 'horizontal' = left-drag on XY plane, 'vertical' = right-drag on plane facing camera. */
  draggingCenterMode: 'horizontal' | 'vertical' | undefined = undefined;

  /** When set, we are dragging a rotation gizmo (0 or 1); Z rotation is driven by mouse angle in XY plane. */
  draggingRotationCorner: 0 | 1 | 2 | 3 | undefined = undefined;
  private rotationDragInitialZ = 0;
  private rotationDragInitialAngle = 0;

  constructor(inputHandler: InputHandler) {
    super('intersection', inputHandler);
    this.visible = false;
    this.reset();
  }

  /** Place gizmos in local space at ±halfSize; boxFrame rotation handles orientation. */
  private updateFaceGizmoPositions() {
    const { x: hx, y: hy, z: hz } = this.halfSize;
    this.rightGizmo.position.set(hx, 0, 0);
    this.leftGizmo.position.set(-hx, 0, 0);
    this.topGizmo.position.set(0, hy, 0);
    this.bottomGizmo.position.set(0, -hy, 0);
    this.frontGizmo.position.set(0, 0, hz);
    this.backGizmo.position.set(0, 0, -hz);
  }

  /** Place rotation gizmos at the four top corners (y = +hy): front-right, front-left, back-right, back-left. */
  private updateRotationGizmoPositions() {
    const { x: hx, y: hy, z: hz } = this.halfSize;
    this.topFrontRightGizmo.position.set(hx, hy, hz);
    this.topFrontLeftGizmo.position.set(-hx, hy, hz);
    this.topBackRightGizmo.position.set(hx, -hy, hz);
    this.topBackLeftGizmo.position.set(-hx, -hy, hz);
  }

  /** Sync mesh scale and gizmo positions from halfSize; mesh stays at origin with no rotation. */
  private syncBoxVisuals() {
    this.intersectionBox.position.set(0, 0, 0);
    this.intersectionBox.rotation.set(0, 0, 0);
    this.intersectionBox.scale.set(this.halfSize.x * 2, this.halfSize.y * 2, this.halfSize.z * 2);
    this.updateFaceGizmoPositions();
    this.updateRotationGizmoPositions();
  }

  /** Threshold: |nz| above this treats the plane as horizontal for "align to 90° edge" logic. */
  private static readonly horizontalNormalZThreshold = 0.99;

  /** Interior angle (radians) is considered "close to 90°" if within this of π/2. */
  private static readonly rightAngleTolerance = (20 * Math.PI) / 180;

  /**
   * Returns the angle (radians) around Z so that the box aligns with the horizontal
   * cross-section of a plane with the given world-space normal. When the plane is
   * horizontal (normal ≈ ±Z), tries to align with an edge that forms a ~90° angle
   * if the hit provides face vertices; otherwise returns 0.
   */
  private static zRotationFromFaceNormal(normal: Vector3, hit?: Intersection): number {
    const nx = normal.x;
    const ny = normal.y;
    const nz = normal.z;
    const xySq = nx * nx + ny * ny;

    if (xySq >= 1e-10) {
      return Math.atan2(-nx, ny);
    }

    if (Math.abs(nz) < IntersectionTool.horizontalNormalZThreshold || !hit?.face || !hit.object) {
      return 0;
    }

    const angle = IntersectionTool.zRotationFromHorizontalFaceEdges(hit);
    return angle ?? 0;
  }

  /**
   * For a horizontal face, if any interior angle is close to 90°, returns the Z rotation
   * (radians) to align the box with one of the sides that form that angle; otherwise null.
   */
  private static zRotationFromHorizontalFaceEdges(hit: Intersection): number | null {
    const face = hit.face as { a: number; b: number; c: number } | undefined;
    const geom = (hit.object as Mesh).geometry;
    const posAttr = geom?.attributes?.position;
    if (!face || !posAttr) {
      return null;
    }

    const va = new Vector3(
      posAttr.getX(face.a),
      posAttr.getY(face.a),
      posAttr.getZ(face.a),
    ).applyMatrix4(hit.object.matrixWorld);
    const vb = new Vector3(
      posAttr.getX(face.b),
      posAttr.getY(face.b),
      posAttr.getZ(face.b),
    ).applyMatrix4(hit.object.matrixWorld);
    const vc = new Vector3(
      posAttr.getX(face.c),
      posAttr.getY(face.c),
      posAttr.getZ(face.c),
    ).applyMatrix4(hit.object.matrixWorld);

    const ab = new Vector3().subVectors(vb, va);
    const bc = new Vector3().subVectors(vc, vb);
    const ca = new Vector3().subVectors(va, vc);
    ab.z = 0;
    bc.z = 0;
    ca.z = 0;

    const lab = ab.length();
    const lbc = bc.length();
    const lca = ca.length();
    if (lab < 1e-8 || lbc < 1e-8 || lca < 1e-8) {
      return null;
    }

    ab.normalize();
    bc.normalize();
    ca.normalize();

    const angleA = Math.acos(MathUtils.clamp(-ab.dot(ca), -1, 1));
    const angleB = Math.acos(MathUtils.clamp(-bc.dot(ab), -1, 1));
    const angleC = Math.acos(MathUtils.clamp(-ca.dot(bc), -1, 1));
    const halfPi = Math.PI / 2;

    const check = (angle: number, edge: Vector3): number | null =>
      Math.abs(angle - halfPi) <= IntersectionTool.rightAngleTolerance
        ? Math.atan2(edge.y, edge.x)
        : null;

    return check(angleA, ab) ?? check(angleB, bc) ?? check(angleC, ca) ?? null;
  }

  private static readonly gizmoProximityThreshold = 1.5;

  private static readonly gizmoHoverThresholdNdc = 0.04;

  /**
   * Returns the single gizmo (face or center) closest to the pointer in screen/NDC space, or undefined.
   * Only one gizmo is hovered at a time.
   */
  getHoveredGizmo(pointerNdc: { x: number; y: number }): HoveredGizmo {
    const camera = cameraManager().getActiveCamera();
    const threeCamera = camera?.getTHREECamera();
    if (!threeCamera) {
      return undefined;
    }

    let best: HoveredGizmo = undefined;
    let bestDistSq =
      IntersectionTool.gizmoHoverThresholdNdc * IntersectionTool.gizmoHoverThresholdNdc;

    const testWorldPos = (worldPos: Vector3, candidate: HoveredGizmo) => {
      const projected = worldPos.clone().project(threeCamera);

      if (projected.z < -1 || projected.z > 1) {
        return;
      }

      const dx = projected.x - pointerNdc.x;
      const dy = projected.y - pointerNdc.y;
      const distSq = dx * dx + dy * dy;

      if (distSq < bestDistSq) {
        bestDistSq = distSq;
        best = candidate;
      }
    };

    for (let i = 0; i < this.faceGizmos.length; i++) {
      if (!this.faceGizmos[i].visible) {
        continue;
      }
      testWorldPos(this.faceGizmos[i].getWorldPosition(this.gizmoWorldPos).clone(), {
        kind: 'face',
        index: i,
      });
    }

    if (this.centerGizmo.visible) {
      testWorldPos(this.boxFrame.getWorldPosition(this.gizmoWorldPos).clone(), { kind: 'center' });
    }

    for (let i = 0; i < this.rotationGizmos.length; i++) {
      if (!this.rotationGizmos[i].visible) {
        continue;
      }
      testWorldPos(this.rotationGizmos[i].getWorldPosition(this.gizmoWorldPos).clone(), {
        kind: 'rotation',
        index: i as 0 | 1 | 2 | 3,
      });
    }

    return best;
  }

  setBoxAndGizmosVisible(visible: boolean) {
    this.intersectionBox.visible = visible;
    const gizmosVisible = visible && this.intersectionActive;
    for (const gizmo of this.faceGizmos) {
      gizmo.visible = gizmosVisible;
    }
    for (const gizmo of this.rotationGizmos) {
      gizmo.visible = gizmosVisible;
    }
    this.centerGizmo.visible = gizmosVisible;
  }

  /**
   * Update halfSize and boxFrame.position from a world-space point where the user dragged the given face.
   * Keeps the opposite face fixed; only the dragged face moves. Center shifts by half the change.
   * Enforces MIN_BOX_HALF_SIZE on each axis.
   */
  applyDraggedFacePoint(faceIndex: number, worldPoint: Vector3) {
    this.boxFrame.updateMatrixWorld(true);

    const localPoint = this.boxFrame.worldToLocal(worldPoint.clone());
    const minH = IntersectionTool.MIN_BOX_HALF_SIZE;

    const localCenter = new Vector3(0, 0, 0);
    const worldCenter = this.boxFrame.getWorldPosition(new Vector3());

    switch (faceIndex) {
      case 0: {
        // front +Z, keep back fixed at -halfSize.z
        const fixed = -this.halfSize.z;
        const dragged = Math.max(fixed + minH * 2, localPoint.z);
        const newCenterZ = (fixed + dragged) * 0.5;
        const newHalfZ = (dragged - fixed) * 0.5;

        localCenter.set(0, 0, newCenterZ);
        this.halfSize.z = newHalfZ;
        break;
      }

      case 1: {
        // back -Z, keep front fixed at +halfSize.z
        const fixed = this.halfSize.z;
        const dragged = Math.min(fixed - minH * 2, localPoint.z);
        const newCenterZ = (dragged + fixed) * 0.5;
        const newHalfZ = (fixed - dragged) * 0.5;

        localCenter.set(0, 0, newCenterZ);
        this.halfSize.z = newHalfZ;
        break;
      }

      case 2: {
        // left -X, keep right fixed at +halfSize.x
        const fixed = this.halfSize.x;
        const dragged = Math.min(fixed - minH * 2, localPoint.x);
        const newCenterX = (dragged + fixed) * 0.5;
        const newHalfX = (fixed - dragged) * 0.5;

        localCenter.set(newCenterX, 0, 0);
        this.halfSize.x = newHalfX;
        break;
      }

      case 3: {
        // right +X, keep left fixed at -halfSize.x
        const fixed = -this.halfSize.x;
        const dragged = Math.max(fixed + minH * 2, localPoint.x);
        const newCenterX = (fixed + dragged) * 0.5;
        const newHalfX = (dragged - fixed) * 0.5;

        localCenter.set(newCenterX, 0, 0);
        this.halfSize.x = newHalfX;
        break;
      }

      case 4: {
        // top +Y, keep bottom fixed at -halfSize.y
        const fixed = -this.halfSize.y;
        const dragged = Math.max(fixed + minH * 2, localPoint.y);
        const newCenterY = (fixed + dragged) * 0.5;
        const newHalfY = (dragged - fixed) * 0.5;

        localCenter.set(0, newCenterY, 0);
        this.halfSize.y = newHalfY;
        break;
      }

      case 5: {
        // bottom -Y, keep top fixed at +halfSize.y
        const fixed = this.halfSize.y;
        const dragged = Math.min(fixed - minH * 2, localPoint.y);
        const newCenterY = (dragged + fixed) * 0.5;
        const newHalfY = (fixed - dragged) * 0.5;

        localCenter.set(0, newCenterY, 0);
        this.halfSize.y = newHalfY;
        break;
      }
    }

    // Local center offset -> world delta; apply to boxFrame.position
    const newWorldCenter = this.boxFrame.localToWorld(localCenter.clone());
    this.boxFrame.position.add(newWorldCenter.sub(worldCenter));
    this.saveBoxCenter();

    this.syncBoxVisuals();
    this.updateRendererClippingPlanes();
  }

  enable() {
    this.reset();
    this.visible = true;
    this.add(this.cursor);
    this.add(this.boxFrame);
    this.boxFrame.add(this.intersectionBox);
    for (const gizmo of this.faceGizmos) {
      this.boxFrame.add(gizmo);
    }
    for (const gizmo of this.rotationGizmos) {
      this.boxFrame.add(gizmo);
    }
    this.boxFrame.add(this.centerGizmo);
  }

  onContextMenuChanged(callback: (contextMenu?: SkyViewContextMenuProps) => void) {
    this.contextMenuCallback = callback;
  }

  disable() {
    this.clearContextMenu();
    this.inputHandler.setCursor('');
    this.reset();
  }

  clearContextMenu() {
    this.contextMenuCallback?.();
  }

  reset() {
    this.visible = false;
    this.remove(this.cursor);
    this.remove(this.boxFrame);
    for (const gizmo of this.faceGizmos) {
      gizmo.visible = false;
    }
    for (const gizmo of this.rotationGizmos) {
      gizmo.visible = false;
    }
    this.centerGizmo.visible = false;
    this.cursor.visible = false;
    this.cursor.position.set(0, 0, 0);
    this.lastIntersection = undefined;
    this.intersectionActive = false;
    this.boxFrame.position.set(0, 0, 0);
    this.boxFrame.rotation.set(0, 0, 0);
    this.boxFrame.quaternion.identity();
    this.boxCenterProjectCoords = undefined;
    this.halfSize.set(0.5, 0.5, 0.5);
    this.intersectionBox.visible = false;
    this.syncBoxVisuals();
    this.draggingFaceIndex = undefined;
    this.draggingCenterMode = undefined;
    this.draggingRotationCorner = undefined;
    this.clearRendererClippingPlanes();
  }

  /**
   * Updates the WebGL renderer's clipping planes from the current bounding box.
   * No-op if webGlRenderer is not set. Call when bounding box or nonce changes.
   */
  private updateRendererClippingPlanes(): void {
    const renderer = this.webGlRenderer;
    if (!renderer) {
      return;
    }
    if (!this.intersectionActive) {
      renderer.clippingPlanes = [];
      return;
    }
    this.boxFrame.updateMatrixWorld(true);
    const { x: hx, y: hy, z: hz } = this.halfSize;
    // Inward-facing normals (kept side = inside the box)
    const localNormals = [
      new Vector3(1, 0, 0), // min X face at (-hx, 0, 0)
      new Vector3(-1, 0, 0), // max X face at (+hx, 0, 0)
      new Vector3(0, 1, 0), // min Y face at (0, -hy, 0)
      new Vector3(0, -1, 0), // max Y face at (0, +hy, 0)
      new Vector3(0, 0, 1), // min Z face at (0, 0, -hz)
      new Vector3(0, 0, -1), // max Z face at (0, 0, +hz)
    ];
    const localPoints = [
      new Vector3(-hx, 0, 0),
      new Vector3(hx, 0, 0),
      new Vector3(0, -hy, 0),
      new Vector3(0, hy, 0),
      new Vector3(0, 0, -hz),
      new Vector3(0, 0, hz),
    ];
    for (let i = 0; i < 6; i++) {
      const worldNormal = localNormals[i].clone().transformDirection(this.boxFrame.matrixWorld);
      this.planePoint.copy(localPoints[i]).applyMatrix4(this.boxFrame.matrixWorld);
      this.planePoint.addScaledVector(worldNormal, -IntersectionTool.CLIPPING_PLANE_EPSILON);
      this.clippingPlanes[i].setFromNormalAndCoplanarPoint(worldNormal, this.planePoint);
    }
    renderer.clippingPlanes = this.clippingPlanes;
  }

  /** Clears the WebGL renderer's clipping planes. Call on menu unmount. */
  private clearRendererClippingPlanes(): void {
    if (this.webGlRenderer) {
      this.webGlRenderer.clippingPlanes = [];
    }
  }

  onMouseDown(e: MouseInputData) {
    this.mousePosition = e.pointer.client;

    if (e.button.type === MouseButtonType.LeftButton) {
      const camera = cameraManager().getActiveCamera();
      if (this.intersectionActive && camera) {
        const hovered = this.getHoveredGizmo(e.pointer.position);

        if (hovered?.kind === 'center') {
          this.draggingCenterMode = 'horizontal';
          this.inputHandler.setCursor('grabbing');
          return false;
        }

        if (hovered?.kind === 'rotation') {
          this.draggingRotationCorner = hovered.index;
          const rayCast = camera.getRay(e.pointer.position.x, e.pointer.position.y);
          this.boxFrame.getWorldPosition(this.gizmoWorldPos);
          this.dragPlane.setFromNormalAndCoplanarPoint(new Vector3(0, 0, 1), this.gizmoWorldPos);
          if (rayCast.ray.intersectPlane(this.dragPlane, this.rayIntersectPoint) !== null) {
            this.rotationDragInitialZ = this.boxFrame.rotation.z;
            this.rotationDragInitialAngle = Math.atan2(
              this.rayIntersectPoint.y - this.gizmoWorldPos.y,
              this.rayIntersectPoint.x - this.gizmoWorldPos.x,
            );
          }
          this.inputHandler.setCursor('grabbing');
          return false;
        }

        if (hovered?.kind === 'face') {
          this.draggingFaceIndex = hovered.index;
          this.inputHandler.setCursor('grabbing');
          return false;
        }
      }
    }

    if (e.button.type === MouseButtonType.RightButton) {
      const camera = cameraManager().getActiveCamera();
      if (this.intersectionActive && camera) {
        const hovered = this.getHoveredGizmo(e.pointer.position);

        if (hovered?.kind === 'center') {
          this.draggingCenterMode = 'vertical';
          this.inputHandler.setCursor('grabbing');
          return false;
        }
      }
    }

    return true;
  }

  async onMouseUp(e: MouseInputData) {
    const wasDraggingFace = this.draggingFaceIndex !== undefined;
    const wasDraggingCenter = this.draggingCenterMode !== undefined;
    const wasDraggingRotation = this.draggingRotationCorner !== undefined;
    this.draggingFaceIndex = undefined;
    this.draggingCenterMode = undefined;
    this.draggingRotationCorner = undefined;

    if (wasDraggingFace || wasDraggingCenter || wasDraggingRotation) {
      const hovered = this.getHoveredGizmo(e.pointer.position);
      this.inputHandler.setCursor(hovered ? 'grab' : '');
    }

    const camera = cameraManager().getActiveCamera();
    if (!this.withinThreshold(e.pointer.client.x, e.pointer.client.y) || !camera) {
      return Promise.resolve(true);
    }

    if (
      e.button.type === MouseButtonType.RightButton &&
      !wasDraggingFace &&
      !wasDraggingCenter &&
      !wasDraggingRotation
    ) {
      this.contextMenuCallback?.({
        pos: {
          x: e.viewportPos?.clientX ?? 0,
          y: e.viewportPos?.clientY ?? 0,
        },
        items: [
          {
            title: t('intersectionTool.contextMenu.startIntersection', { ns: 'skyviewToolbar' }),
            action: () => {
              this.intersectionActive = true;
              this.updateRendererClippingPlanes();
              this.contextMenuCallback?.();
            },
            disabled: this.intersectionActive,
          },
          {
            title: t('intersectionTool.contextMenu.resetIntersection', { ns: 'skyviewToolbar' }),
            action: () => {
              this.intersectionActive = false;
              this.updateRendererClippingPlanes();
              this.contextMenuCallback?.();
            },
            disabled: !this.intersectionActive,
          },
        ],
      });
    }

    return Promise.resolve(true);
  }

  onMouseMove(e: MouseInputData): boolean {
    this.clearContextMenu();
    const camera = cameraManager().getActiveCamera();

    if (!camera) {
      return false;
    }

    if (this.intersectionActive) {
      const rayCast = camera.getRay(e.pointer.position.x, e.pointer.position.y);

      if (this.draggingCenterMode === 'horizontal') {
        this.boxFrame.getWorldPosition(this.gizmoWorldPos);

        // Z-up => horizontal plane is XY => normal = +Z
        this.dragPlane.setFromNormalAndCoplanarPoint(new Vector3(0, 0, 1), this.gizmoWorldPos);

        if (rayCast.ray.intersectPlane(this.dragPlane, this.rayIntersectPoint) !== null) {
          this.boxFrame.position.copy(this.rayIntersectPoint);
          this.saveBoxCenter();
          this.updateRendererClippingPlanes();
        }
      } else if (this.draggingCenterMode === 'vertical') {
        const cameraDir = new Vector3();
        camera.getTHREECamera().getWorldDirection(cameraDir);
        this.boxFrame.getWorldPosition(this.gizmoWorldPos);
        this.dragPlane.setFromNormalAndCoplanarPoint(cameraDir, this.gizmoWorldPos);
        if (rayCast.ray.intersectPlane(this.dragPlane, this.rayIntersectPoint) !== null) {
          this.boxFrame.position.z = this.rayIntersectPoint.z;
          this.saveBoxCenter();
          this.updateRendererClippingPlanes();
        }
      } else if (this.draggingRotationCorner !== undefined) {
        this.boxFrame.getWorldPosition(this.gizmoWorldPos);
        this.dragPlane.setFromNormalAndCoplanarPoint(new Vector3(0, 0, 1), this.gizmoWorldPos);
        if (rayCast.ray.intersectPlane(this.dragPlane, this.rayIntersectPoint) !== null) {
          const currentAngle = Math.atan2(
            this.rayIntersectPoint.y - this.gizmoWorldPos.y,
            this.rayIntersectPoint.x - this.gizmoWorldPos.x,
          );
          this.boxFrame.rotation.z =
            this.rotationDragInitialZ + (currentAngle - this.rotationDragInitialAngle);
          this.updateRendererClippingPlanes();
        }
      } else if (this.draggingFaceIndex !== undefined) {
        const faceIndex = this.draggingFaceIndex;
        const localAxis = IntersectionTool.faceNormals[faceIndex].clone().normalize();
        const worldAxis = localAxis.clone().applyQuaternion(this.boxFrame.quaternion).normalize();

        const cameraDir = new Vector3();
        camera.getTHREECamera().getWorldDirection(cameraDir);

        const planeNormal = new Vector3()
          .crossVectors(cameraDir, worldAxis)
          .cross(worldAxis)
          .normalize();

        if (planeNormal.lengthSq() < 1e-8) {
          planeNormal.set(0, 1, 0).cross(worldAxis).cross(worldAxis).normalize();
        }

        this.faceGizmos[faceIndex].getWorldPosition(this.gizmoWorldPos);
        this.dragPlane.setFromNormalAndCoplanarPoint(planeNormal, this.gizmoWorldPos);

        if (rayCast.ray.intersectPlane(this.dragPlane, this.rayIntersectPoint) !== null) {
          const centerWorld = this.boxFrame.getWorldPosition(new Vector3());
          this.dragLineStart
            .copy(centerWorld)
            .addScaledVector(worldAxis, -IntersectionTool.gizmoDragLineHalfLength);
          this.dragLineEnd
            .copy(centerWorld)
            .addScaledVector(worldAxis, IntersectionTool.gizmoDragLineHalfLength);

          const { point } = findClosestPointOnLine(
            this.dragLineStart,
            this.dragLineEnd,
            this.rayIntersectPoint,
          );

          this.applyDraggedFacePoint(faceIndex, point);
        }
      }

      if (
        this.draggingFaceIndex === undefined &&
        this.draggingCenterMode === undefined &&
        this.draggingRotationCorner === undefined
      ) {
        this.expandedPickBox.setFromObject(this.intersectionBox).expandByScalar(BOX_HIT_MARGIN);
        const hitsBox =
          rayCast.ray.intersectBox(this.expandedPickBox, this.rayIntersectPoint) !== null;
        this.setBoxAndGizmosVisible(hitsBox);
      }

      const hovered = this.getHoveredGizmo(e.pointer.position);

      for (let i = 0; i < this.faceGizmos.length; i++) {
        this.faceGizmos[i].setColor(
          hovered?.kind === 'face' && hovered.index === i ? GIZMO_HOVER_COLOR : GIZMO_DEFAULT_COLOR,
        );
      }

      this.centerGizmo.setColor(
        hovered?.kind === 'center' ? GIZMO_HOVER_COLOR : GIZMO_CENTER_COLOR,
      );

      for (let i = 0; i < this.rotationGizmos.length; i++) {
        this.rotationGizmos[i].setColor(
          hovered?.kind === 'rotation' && hovered.index === i
            ? GIZMO_HOVER_COLOR
            : GIZMO_DEFAULT_COLOR,
        );
      }

      if (
        this.draggingFaceIndex !== undefined ||
        this.draggingCenterMode !== undefined ||
        this.draggingRotationCorner !== undefined
      ) {
        this.inputHandler.setCursor('grabbing');
      } else if (hovered) {
        this.inputHandler.setCursor('grab');
      } else {
        this.inputHandler.setCursor('');
      }
      return (
        this.draggingFaceIndex === undefined &&
        this.draggingCenterMode === undefined &&
        this.draggingRotationCorner === undefined
      );
    }

    const rayCast = camera.getRay(e.pointer.position.x, e.pointer.position.y);

    const pixelSnapThreshold = 20;
    const minPickingDistance = 0.1;
    const maxPickingDistance = 350;

    setRayCasterParamsBasedOnCamera(
      camera.getTHREECamera(),
      rayCast,
      pixelSnapThreshold,
      MathUtils.clamp(
        this.cursor.position.distanceTo(rayCast.ray.origin),
        minPickingDistance,
        maxPickingDistance,
      ),
    );

    let hits = rayCast.intersectObjects(AssetManager.instance.getAssets(), true);
    let filteredHits = hits.filter((hit) =>
      this.pointIsInsideIntersectionArea(hit.point, this.clippingPlanes),
    );

    // Intersect with the scene only if no assets are intersected.
    if (filteredHits.length === 0) {
      const objects: Object3D[] = [];
      if (PointCloudHandle.instance.visible) {
        objects.push(...(PointCloudHandle.instance.activePointCloud?.children ?? []));
      }

      const activeTiledModel = Tiled3DModelHandle.instance.getActiveTiledModel();
      if (activeTiledModel?.visible) {
        objects.push(...(activeTiledModel?.children ?? []));
      }

      hits = rayCast.intersectObjects(objects, false);
      filteredHits = hits.filter((hit) =>
        this.pointIsInsideIntersectionArea(hit.point, this.clippingPlanes),
      );
    }

    // Prefer face (Mesh) hits over lines and points
    const hitPriority = (hit: Intersection): number => {
      const obj = hit.object;
      if (obj instanceof Mesh) {
        return 0;
      }
      if (obj instanceof Line || obj instanceof LineSegments) {
        return 1;
      }
      if (obj instanceof Points) {
        return 2;
      }
      return 3;
    };
    filteredHits.sort((a, b) => hitPriority(a) - hitPriority(b));

    this.setBoxAndGizmosVisible(isDefined(filteredHits[0]?.object));
    if (filteredHits[0]?.point) {
      this.cursor.position.copy(filteredHits[0]?.point);
      this.lastIntersection = filteredHits[0];

      const hit = this.lastIntersection;
      const zAngle =
        hit.face?.normal && hit.object
          ? IntersectionTool.zRotationFromFaceNormal(
              hit.face.normal.clone().transformDirection(hit.object.matrixWorld),
              hit,
            )
          : 0;

      const aabb = new Box3().setFromObject(this.lastIntersection.object);
      const center = aabb.getCenter(new Vector3());
      const { min, max } = aabb;

      const cosZ = Math.cos(-zAngle);
      const sinZ = Math.sin(-zAngle);
      let minX = Infinity,
        maxX = -Infinity,
        minY = Infinity,
        maxY = -Infinity,
        minZ = Infinity,
        maxZ = -Infinity;
      for (const sx of [min.x, max.x]) {
        for (const sy of [min.y, max.y]) {
          for (const sz of [min.z, max.z]) {
            const dx = sx - center.x;
            const dy = sy - center.y;
            const dz = sz - center.z;
            const rx = dx * cosZ + dy * sinZ;
            const ry = -dx * sinZ + dy * cosZ;
            minX = Math.min(minX, rx);
            maxX = Math.max(maxX, rx);
            minY = Math.min(minY, ry);
            maxY = Math.max(maxY, ry);
            minZ = Math.min(minZ, dz);
            maxZ = Math.max(maxZ, dz);
          }
        }
      }

      const minHalf = IntersectionTool.MIN_BOX_HALF_SIZE;
      this.boxFrame.position.copy(center);
      this.saveBoxCenter();
      this.boxFrame.rotation.set(0, 0, zAngle);
      this.halfSize.set(
        Math.max((maxX - minX) * 0.5, minHalf),
        Math.max((maxY - minY) * 0.5, minHalf),
        Math.max((maxZ - minZ) * 0.5, minHalf),
      );

      this.syncBoxVisuals();
    }

    this.inputHandler.setCursor('');
    return true;
  }

  pointIsInsideIntersectionArea(point: Vector3, _planes: Plane[]) {
    if (!this.intersectionActive) {
      return true;
    }
    const local = this.boxFrame.worldToLocal(point.clone());
    return (
      Math.abs(local.x) <= this.halfSize.x &&
      Math.abs(local.y) <= this.halfSize.y &&
      Math.abs(local.z) <= this.halfSize.z
    );
  }

  onProjectProjectionUpdated(): boolean {
    if (this.boxCenterProjectCoords) {
      const newLocalPos = CoordinateManager.getInstance().projectCoordsToLocal(
        this.boxCenterProjectCoords,
      );
      this.boxFrame.position.copy(newLocalPos);
      this.updateRendererClippingPlanes();
    }
    return true;
  }
}
